/**
 * Pure TEA update for status-bar / native-menu chrome actions.
 *
 * Shell: `useEditorChromeStore.ts` (Pinia). Handlers are registered by
 * DocumentEditor / EditorShell; the shell forwards calls to whatever is
 * currently in the Model.
 */
import { assertNever } from "../lib/assertNever";
import { isJust, toMaybe } from "../lib/maybe";

export type EditorChromeActions = {
  toggleHistory: () => void;
  toggleComments: () => void;
  closeHistory: () => void;
  openFind: () => void;
  findNext: () => void;
  selectNextOccurrence: () => void;
  /**
   * Optional: EditorShell owns the thesaurus dialog. DocumentEditor
   * omits this so Shell's handler is not overwritten.
   */
  openThesaurus?: () => void;
};

/** Handlers as held in the Model: every slot is always callable. */
export type EditorChromeHandlers = Required<EditorChromeActions>;

export type EditorChromeModel = {
  readonly handlers: EditorChromeHandlers;
  readonly registered: boolean;
};

const noop = (): void => {};

export const initialEditorChromeModel: EditorChromeModel = {
  handlers: {
    toggleHistory: noop,
    toggleComments: noop,
    closeHistory: noop,
    openFind: noop,
    findNext: noop,
    selectNextOccurrence: noop,
    openThesaurus: noop,
  },
  registered: false,
};

export type EditorChromeMsg = {
  readonly type: "Register";
  readonly actions: EditorChromeActions;
};

/** No effects: registering only swaps handlers in the Model. */
export type EditorChromeCmd = never;

export function updateEditorChrome(
  model: EditorChromeModel,
  msg: EditorChromeMsg,
): readonly [EditorChromeModel, readonly EditorChromeCmd[]] {
  switch (msg.type) {
    case "Register": {
      const { actions } = msg;
      const openThesaurus = toMaybe(actions.openThesaurus);
      return [
        {
          handlers: {
            toggleHistory: actions.toggleHistory,
            toggleComments: actions.toggleComments,
            closeHistory: actions.closeHistory,
            openFind: actions.openFind,
            findNext: actions.findNext,
            selectNextOccurrence: actions.selectNextOccurrence,
            openThesaurus: isJust(openThesaurus)
              ? openThesaurus.value
              : model.handlers.openThesaurus,
          },
          registered: true,
        },
        [],
      ];
    }
    default:
      // Single-variant Msg: `msg` itself is not narrowed, its tag is.
      return assertNever(msg.type);
  }
}
